import { z } from "zod"

import type { MediaItemType, StoreConfig } from "./types"

/** BMS caps a caption at 140 characters; a longer one is cut, not dropped. */
export const GALLERY_CAPTION_MAX_LENGTH = 140

/** A gallery entry with its fields validated. */
export interface ResolvedGalleryItem {
  url: string
  type: MediaItemType
  /** `null` ⇒ no caption; label it with the "<store name> photo N" fallback. */
  caption: string | null
}

/** `StoreConfig.gallery` narrowed to what the page renders. */
export interface ResolvedGallery {
  /** `null` ⇒ use the translated default heading. */
  title: string | null
  /** `null` ⇒ hide the intro. */
  intro: string | null
  /** Never empty, in staff display order. */
  media: ResolvedGalleryItem[]
}

const optionalText = z
  .string()
  .trim()
  .min(1)
  .nullish()
  .catch(undefined)
  .transform((value) => value ?? null)

const mediaItemSchema = z
  .object({
    url: z.url({ protocol: /^https?$/ }),
    type: z.unknown().optional(),
    caption: z.unknown().optional(),
  })
  .transform(
    ({ url, type, caption }): ResolvedGalleryItem => ({
      url,
      type: type === "video" ? "video" : "image",
      caption:
        typeof caption === "string" && caption.trim()
          ? caption.trim().slice(0, GALLERY_CAPTION_MAX_LENGTH)
          : null,
    })
  )

/**
 * Narrow a tenant's raw `gallery`. Returns `null` — no page, no nav links —
 * unless `isEnabled` is exactly `true` and at least one entry survives:
 *   - an entry without a valid `http(s)` URL is dropped (a half-filled row
 *     never renders a broken tile); the rest keep their order;
 *   - a `type` other than `video` (absent, unknown) is a photo;
 *   - blank title/intro/caption read as absent; a caption past 140 characters
 *     is cut.
 */
export function resolveGallery(
  config: Pick<StoreConfig, "gallery">
): ResolvedGallery | null {
  const gallery = config.gallery
  if (gallery?.isEnabled !== true || !Array.isArray(gallery.media)) return null
  const media = gallery.media.flatMap((item) => {
    const parsed = mediaItemSchema.safeParse(item)
    return parsed.success ? [parsed.data] : []
  })
  if (media.length === 0) return null
  return {
    title: optionalText.parse(gallery.title),
    intro: optionalText.parse(gallery.intro),
    media,
  }
}

/** The visibility rule shared by `/gallery` and every layout's nav link. */
export function isGalleryVisible(config: Pick<StoreConfig, "gallery">): boolean {
  return resolveGallery(config) !== null
}

/**
 * Alt text (or a video's `aria-label`) for the gallery entry at 1-based
 * `position`: its caption, else `fallback(position)` — the localized
 * "<store name> photo N".
 */
export function galleryItemLabel(
  item: Pick<ResolvedGalleryItem, "caption">,
  position: number,
  fallback: (position: number) => string
): string {
  return item.caption ?? fallback(position)
}
